import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogPortal, DialogOverlay } from '../ui/dialog';
import { Loader2, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../hooks/useAuth';

interface CreateFollowUpDialogProps {
    appointmentId: string;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export function CreateFollowUpDialog({ appointmentId, isOpen, onClose, onSuccess }: CreateFollowUpDialogProps) {
    const { token } = useAuthStore();
    const [type, setType] = useState('call_carrier');
    const [dueDate, setDueDate] = useState('');
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!type || !token) return;

        setIsSubmitting(true);
        try {
            const res = await fetch(`/api/appointments/${appointmentId}/follow-up`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    type,
                    dueDate: dueDate || null,
                    notes,
                }),
            });

            if (!res.ok) throw new Error('Failed to create follow-up');

            toast.success('Follow-up created');
            onSuccess();
            handleClose();
        } catch (error) {
            console.error(error);
            toast.error('Failed to create follow-up');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setType('call_carrier');
        setDueDate('');
        setNotes('');
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
            <DialogPortal>
                <DialogOverlay />
                <DialogContent className="max-w-md bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800">
                    <DialogHeader>
                        <DialogTitle>Create Follow-up Task</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Action Type</label>
                            <select
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                                className="w-full field"
                            >
                                <option value="call_carrier">Call Carrier</option>
                                <option value="request_docs">Request Documents</option>
                                <option value="reschedule">Reschedule</option>
                                <option value="investigate">Investigate Issue</option>
                                <option value="other">Other</option>
                            </select>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium">Due Date (Optional)</label>
                            <div className="relative">
                                <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
                                <input
                                    type="date"
                                    value={dueDate}
                                    onChange={(e) => setDueDate(e.target.value)}
                                    className="w-full field pl-10"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium">Notes</label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Add details about this task..."
                                className="w-full field min-h-[100px]"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <button onClick={handleClose} className="btn btn-ghost" disabled={isSubmitting}>
                            Cancel
                        </button>
                        <button onClick={handleSubmit} className="btn btn-primary" disabled={isSubmitting}>
                            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Create Task
                        </button>
                    </DialogFooter>
                </DialogContent>
            </DialogPortal>
        </Dialog>
    );
}
